import { useCallback, useMemo, useState } from "react";
import { ALL_CAPABILITIES } from "../data";
import {
  defaultFilters,
  filterModels,
  type CatalogFilters,
  type CatalogSort,
} from "../lib/filters";
import { ModelTable } from "../components/ModelTable";
import { CompareTray } from "../components/CompareTray";
import { CompareModal } from "../components/CompareModal";
import { useCatalogData } from "../components/CatalogDataContext";
import { useCompare } from "../components/CompareContext";
import { IntentSearch } from "../components/IntentSearch";
import type { CapabilityStrengths } from "../types";
import { STRENGTH_KEYS, STRENGTH_LABELS } from "../types";
import { MetricBand } from "../components/ui/MetricBand";
import { StatusDot } from "../components/ui/StatusDot";

function filtersActive(f: CatalogFilters): boolean {
  return (
    f.q !== "" ||
    f.modality !== "all" ||
    f.providerId !== "all" ||
    f.capability !== "all" ||
    f.strength !== "all" ||
    f.qualityRank !== "all" ||
    f.confidence !== "all" ||
    f.status !== "all" ||
    f.maxPrice != null ||
    f.sort !== "name" ||
    f.nameQuery !== "" ||
    (f.modelIds != null && f.modelIds.length > 0)
  );
}

export function CatalogPage() {
  const { models, providers, meta } = useCatalogData();
  const { openCompare } = useCompare();
  const [filters, setFilters] = useState<CatalogFilters>(defaultFilters);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const providerName = useCallback(
    (id: string) => providers.find((p) => p.id === id)?.name ?? id,
    [providers],
  );

  const filtered = useMemo(
    () => filterModels(models, filters, providerName),
    [filters, models, providerName],
  );

  const set = <K extends keyof CatalogFilters>(k: K, v: CatalogFilters[K]) =>
    setFilters((f) => ({ ...f, [k]: v }));

  const active = filtersActive(filters);

  const reset = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const imageCount = models.filter((m) => m.modality === "image").length;
  const videoCount = models.filter((m) => m.modality === "video").length;

  return (
    <div>
      <p className="eyebrow">01 / CATALOG</p>
      <div className="page-header-row" style={{ marginBottom: "0.85rem" }}>
        <h1 className="tl-title" style={{ fontSize: "clamp(1.85rem, 4vw, 3rem)" }}>
          Model API pricing
          <span className="ctx"> for generative media.</span>
        </h1>
        <StatusDot kind="live" label="INTENT · LIVE PRICES" />
      </div>
      <p className="hero-lead" style={{ marginTop: 0, marginBottom: "1.5rem" }}>
        One control plane across providers — normalized units, quality ranks,
        capability matrix, and docs. Ask in plain language; Jev maps intent onto
        saved categorization and updates the grid. Say “compare A and B” to open
        a spider modal.
      </p>

      <MetricBand
        items={[
          { label: "Providers", value: providers.length },
          { label: "Models", value: models.length },
          { label: "Image", value: imageCount, tone: "amber" },
          { label: "Video", value: videoCount },
        ]}
      />

      <div
        className="yuri-footer-row"
        style={{ marginTop: "-0.75rem", marginBottom: "1.75rem" }}
      >
        <span className="meta-left">Normalized · quality ranks · capability matrix</span>
        <div className="cta-row">
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => setAdvancedOpen((o) => !o)}
          >
            {advancedOpen ? "Hide advanced" : "Advanced filters"}
          </button>
          <span className="muted" style={{ fontSize: "0.85rem" }}>
            Tip: “compare A and B” opens the spider modal
          </span>
        </div>
      </div>

      <IntentSearch
        filters={filters}
        resultCount={filtered.length}
        onFiltersChange={setFilters}
        onCompare={(ids) => openCompare(ids)}
        onReset={reset}
      />

      {advancedOpen ? (
        <div className="filters-sticky" id="catalog-filters">
          <div className="filters">
            <div className="filters-toolbar">
              <div className="search-wrap">
                <input
                  id="q"
                  aria-label="Search"
                  placeholder="Keyword search models, providers…"
                  value={filters.q}
                  onChange={(e) => set("q", e.target.value)}
                />
              </div>

              <div className="segmented" role="group" aria-label="Modality">
                {(
                  [
                    ["all", "All"],
                    ["image", "Image"],
                    ["video", "Video"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={filters.modality === value ? "active" : ""}
                    onClick={() => set("modality", value)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="toolbar-select">
                <select
                  aria-label="Provider"
                  value={filters.providerId}
                  onChange={(e) => set("providerId", e.target.value)}
                >
                  <option value="all">All providers</option>
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="toolbar-select">
                <select
                  aria-label="Capability"
                  value={filters.capability}
                  onChange={(e) => set("capability", e.target.value)}
                >
                  <option value="all">All capabilities</option>
                  {ALL_CAPABILITIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div className="toolbar-select">
                <select
                  aria-label="Strength"
                  value={filters.strength}
                  onChange={(e) =>
                    set("strength", e.target.value as CatalogFilters["strength"])
                  }
                >
                  <option value="all">Any strength</option>
                  {STRENGTH_KEYS.map((k) => (
                    <option key={k} value={k}>
                      {STRENGTH_LABELS[k]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="toolbar-select">
                <select
                  aria-label="Quality rank"
                  value={filters.qualityRank}
                  onChange={(e) =>
                    set(
                      "qualityRank",
                      e.target.value as CatalogFilters["qualityRank"],
                    )
                  }
                >
                  <option value="all">Any quality</option>
                  <option value="sota">SOTA</option>
                  <option value="good">Good</option>
                  <option value="usable">Usable</option>
                </select>
              </div>

              <div className="toolbar-select">
                <select
                  aria-label="Price confidence"
                  value={filters.confidence}
                  onChange={(e) =>
                    set(
                      "confidence",
                      e.target.value as CatalogFilters["confidence"],
                    )
                  }
                >
                  <option value="all">Any confidence</option>
                  <option value="verified">Verified</option>
                  <option value="estimated">Estimated</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>

              <div className="toolbar-select">
                <select
                  aria-label="Status"
                  value={filters.status}
                  onChange={(e) => set("status", e.target.value)}
                >
                  <option value="all">Any status</option>
                  <option value="ga">GA</option>
                  <option value="preview">Preview</option>
                  <option value="beta">Beta</option>
                  <option value="deprecated">Deprecated</option>
                </select>
              </div>

              <div className="toolbar-select no-chevron">
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  aria-label="Max normalized price"
                  placeholder="Max $"
                  value={filters.maxPrice ?? ""}
                  onChange={(e) =>
                    set(
                      "maxPrice",
                      e.target.value === "" ? null : Number(e.target.value),
                    )
                  }
                />
              </div>

              <div className="toolbar-select">
                <select
                  aria-label="Sort"
                  value={
                    filters.sort === "verified" ? "verified-desc" : filters.sort
                  }
                  onChange={(e) => set("sort", e.target.value as CatalogSort)}
                >
                  <option value="name">Sort: Name ↑</option>
                  <option value="name-desc">Sort: Name ↓</option>
                  <option value="quality">Sort: Quality ↑</option>
                  <option value="quality-desc">Sort: Quality ↓</option>
                  <option value="price-asc">Sort: Price ↑</option>
                  <option value="price-desc">Sort: Price ↓</option>
                  <option value="verified-desc">Sort: Verified ↓</option>
                  <option value="provider">Sort: Provider ↑</option>
                </select>
              </div>

              <button
                type="button"
                className="btn-reset"
                disabled={!active}
                onClick={reset}
              >
                Reset
              </button>
            </div>

            <div className="filters-meta">
              <span>
                Showing{" "}
                <strong style={{ color: "var(--muted)" }}>{filtered.length}</strong>{" "}
                of {models.length} models
                {meta.lastRefreshAt ? " · KV overrides active" : ""}
              </span>
              <span>Select up to 4 to compare</span>
            </div>
          </div>

          <div className="strength-chips" role="group" aria-label="Strength filters">
            {(
              [
                ["edit", "good at edit"],
                ["lora", "LoRA"],
                ["textToVideo", "Text→Video"],
                ["imageToVideo", "Image→Video"],
                ["audioInVideo", "Audio"],
                ["upscale", "Upscale"],
                ["styleReference", "Style ref"],
                ["training", "Training"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`chip${filters.strength === key ? " active" : ""}`}
                onClick={() =>
                  set(
                    "strength",
                    filters.strength === key
                      ? "all"
                      : (key as keyof CapabilityStrengths),
                  )
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="filters-meta" style={{ margin: "0.35rem 0 0.85rem" }}>
          <span>
            Showing{" "}
            <strong style={{ color: "var(--muted)" }}>{filtered.length}</strong> of{" "}
            {models.length} models
            {meta.lastRefreshAt ? " · KV overrides active" : ""}
          </span>
          <span>Select up to 4 to compare</span>
        </div>
      )}

      <p className="eyebrow" style={{ marginTop: "1.25rem" }}>
        03 / DATA GRID
      </p>
      <ModelTable
        models={filtered}
        sort={filters.sort}
        onSortChange={(next) => set("sort", next)}
      />
      <CompareTray />
      <CompareModal />
    </div>
  );
}
